<template>
  <pdl-dialog
    :visible="showDialog"
    :close-on-press-escape="false"
    :close-on-click-modal="false"
    z-index="16000011"
    qaid="bundle-dialog"
    custom-class="bundle-dialog"
    :append-to-body="true"
    :width="viewBreakpointMoreThan('small') ? '75%' : '100%'"
    :top="viewBreakpointMoreThan('small') ? '15vh' : '60px'"
    @close="onCancel"
  >
    <template v-if="!isBundleDataEmpty" #title>
      <section-header size="lg">
        <template slot="content">
          <heading level="1" size="lg" qaid="bundle-dialog-heading" v-html="bp('bundleModalHeading')" />
          <subheading qaid="bundle-dialog-subHeading" v-html="bp('bundleModalSubHeading')" />
        </template>
      </section-header>
    </template>

    <pdl-loading :is-loading="dataIsLoading">
      <div v-for="(bundleMainVariant, index) of bundleMainProducts" :key="index">
        <bundling-primary-product
          v-if="!dataIsLoading"
          :base-properties="baseProperties"
          :show-dialog="showDialog"
          :bundle-main-product-data="bundleMainVariant"
          @update-variant-qty="updateVariantQty"
        />
      </div>

      <heading
        v-if="!isBundleDataEmpty && !dataIsLoading"
        level="2"
        size="md"
        class="mt-4"
        qaid="bundle-dialog-additional-parts-heading"
        v-html="bp('additionalParts')"
      />

      <div v-if="!isBundleDataEmpty && !dataIsLoading" class="b2b-grid-group">
        <bundling-child-product
          v-for="product in getChildProductList"
          :key="product.code"
          :base-properties="baseProperties"
          :apple-table-headers="appleTableHeaders"
          :child-product="product"
          class="b2b-grid-group__section"
        />
      </div>
    </pdl-loading>

    <template #footer>
      <div class="buttons buttons--right mt-5">
        <!-- Add to cart button -->
        <pdl-button
          qaid="bundle-dialog-cart-button"
          :disabled="dataIsLoading"
          primary
          icon="shopping_cart"
          type="submit"
          @click="onAddToCart"
        >
          <span v-html="addToCartButtonCaption" />
        </pdl-button>

        <!-- Cancel Button -->
        <pdl-button qaid="bundle-dialog-cancel-button" secondary @click="$emit('cancel')">
          <span v-html="bp('cancel')" />
        </pdl-button>
      </div>
    </template>
  </pdl-dialog>
</template>

<script>
import get from 'lodash/get';
import {PdlLoading} from '@pedal/pdl-loading';
import {PdlDialog} from '@pedal/pdl-dialog';
import {mapState, mapActions, mapMutations, mapGetters} from 'vuex';
import BundlingPrimaryProduct from '@/components/containers/pdp/BundlingPrimaryProduct';
import BundlingChildProduct from '@/components/containers/pdp/BundlingChildProduct';
import SectionHeader from '@/components/SectionHeader';
import Heading from '@/components/Heading';
import Subheading from '@/components/Subheading';
import viewSize from '@/mixins/view-size';
import {PdlButton} from '@pedal/pdl-button';
import removeDuplicateProducts from '@/modules/ProductBundle';

export default {
  components: {
    PdlLoading,
    BundlingPrimaryProduct,
    BundlingChildProduct,
    SectionHeader,
    Heading,
    Subheading,
    PdlButton,
    PdlDialog,
  },
  mixins: [viewSize],
  props: {
    baseProperties: {
      type: Array,
      default: () => [],
    },
    showDialog: {
      type: Boolean,
      default: false,
    },
    appleTableHeaders: {
      type: Array,
      default: () => [],
    },
    addToCartButtonCaption: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      dataIsLoading: false,
    };
  },

  computed: {
    // Get all the child options from all the active bundle templates.
    getChildProductList() {
      let bundles = [];

      this.activeIds.forEach((id) => {
        const bundleItem = get(this.bundleData, [id, 'bundles'], []);

        bundles = bundles.concat(bundleItem);
      });

      // Passing all the bundle product to get unique child products(Remove duplicates).
      return removeDuplicateProducts(bundles);
    },

    ...mapGetters('productBundle', ['isBundleDataEmpty']),
    ...mapState('productBundle', ['bundleData', 'bundleTemplateIds', 'activeIds', 'bundleMainProducts']),
    ...mapState('productBundle', ['isVariantsChanged']),
    ...mapState('skuGrid', ['isBundleModal', 'skuEntriesWithBundle']),
  },

  watch: {
    showDialog(isVisible) {
      // call API when modal is opened and bundleData is empty.
      if (isVisible && this.isVariantsChanged) {
        this.getBundleResponse();
      }
    },
  },

  methods: {
    getBundleResponse() {
      this.dataIsLoading = true;
      this.fetchBundleProducts()
        .then((response) => {
          if (!response) throw new Error();
          this.dataIsLoading = false;
        })
        .catch(() => {
          this.$emit('error');
        });
    },

    onAddToCart() {
      this.addBundleSkuToCart(this.bp('addToCartFail'));
      this.$emit('add-to-cart');
    },

    onCancel() {
      if (!this.isBundleModal) {
        window.vm.toast({
          type: 'alert',
          showClose: true,
          duration: 0,
          message: this.bp('bundleCancelMessage'),
        });
        //   Set the entries to skuEntriesWithBundle again in case user close dialogs without adding anything
        this.setEntries(this.skuEntriesWithBundle);
        this.$emit('cancel');
      }
      this.clearRegisterInputs();
    },

    updateVariantQty(payload) {
      this.isBundleInput(true);
      this.updateVariantQuantity({
        qty: payload.qty,
        sku: payload.sku,
        input: payload.input,
        productType: 'bundleProducts',
        callback: () => '',
      });
    },

    ...mapMutations('savedLists', ['setEntries']),
    ...mapActions('skuGrid', ['clearRegisterInputs', 'addBundleSkuToCart']),
    ...mapMutations('skuGrid', ['updateVariantQuantity', 'isBundleInput']),
    ...mapActions('productBundle', ['fetchBundleProducts']),
  },
};
</script>
